/**
 * AES-256-GCM encrypt/decrypt with zlib compression.
 *
 * Compatibility notes vs Python (PyCryptodome):
 *   - Python: cipher.encrypt_and_digest() → (ciphertext, 16-byte tag) separately
 *   - Web Crypto: encrypt() → ciphertext + tag concatenated (last 16 bytes)
 *   - We split/join accordingly for header compatibility
 */

import pako from "pako";
import { randomBytes, sha256 } from "./utils.js";

const NONCE_LENGTH = 12;
const TAG_LENGTH = 16;

export interface EncryptedPayload {
  ciphertext: Uint8Array;
  nonce: Uint8Array; // 12 bytes
  authTag: Uint8Array; // 16 bytes
  compressedSize: number;
  originalHash: Uint8Array; // SHA-256 of plaintext (before compression)
}

/**
 * Compress and encrypt payload with AES-256-GCM.
 * Hash is computed on raw plaintext BEFORE compression (matches Python).
 */
export async function encryptPayload(
  key: Uint8Array,
  plaintext: Uint8Array
): Promise<EncryptedPayload> {
  const originalHash = await sha256(plaintext);
  const compressed = pako.deflate(plaintext, { level: 9 });
  const nonce = randomBytes(NONCE_LENGTH);

  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    key as BufferSource,
    "AES-GCM",
    false,
    ["encrypt"]
  );

  // Web Crypto appends 16-byte tag to ciphertext
  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce as BufferSource, tagLength: TAG_LENGTH * 8 },
    cryptoKey,
    compressed as BufferSource
  );

  const combined = new Uint8Array(encrypted);
  const ciphertext = combined.slice(0, combined.length - TAG_LENGTH);
  const authTag = combined.slice(combined.length - TAG_LENGTH);

  return {
    ciphertext,
    nonce,
    authTag,
    compressedSize: compressed.length,
    originalHash,
  };
}

/**
 * Decrypt and decompress payload.
 * Recombines ciphertext + authTag for Web Crypto, then zlib decompresses.
 */
export async function decryptPayload(
  key: Uint8Array,
  ciphertext: Uint8Array,
  nonce: Uint8Array,
  authTag: Uint8Array
): Promise<Uint8Array> {
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    key as BufferSource,
    "AES-GCM",
    false,
    ["decrypt"]
  );

  // Web Crypto expects ciphertext + tag concatenated
  const combined = new Uint8Array(ciphertext.length + authTag.length);
  combined.set(ciphertext);
  combined.set(authTag, ciphertext.length);

  let compressed: ArrayBuffer;
  try {
    compressed = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: nonce as BufferSource, tagLength: TAG_LENGTH * 8 },
      cryptoKey,
      combined as BufferSource
    );
  } catch {
    throw new Error(
      "Authentication failed: data may be tampered or wrong passphrase."
    );
  }

  return pako.inflate(new Uint8Array(compressed));
}
